/**
 * 网络请求工具 封装umi-request
 * 更详细的 api 文档: https://github.com/umijs/umi-request
 */

import { message } from "antd";
import { history } from "umi";
import type { RequestOptionsInit } from "umi-request";
import { extend } from "umi-request";

// codeMessage仅供参考 具体根据和后端协商,在详细定义.
const codeMessage: { [propName: string]: string } = {
  200: "服务器成功返回请求的数据。",
  201: "新建或修改数据成功。",
  202: "一个请求已经进入后台排队（异步任务）。",
  204: "删除数据成功。",
  400: "发出的请求有错误，服务器没有进行新建或修改数据的操作。",
  401: "用户没有权限（令牌、用户名、密码错误）。",
  403: "用户得到授权，但是访问是被禁止的。",
  404: "发出的请求针对的是不存在的记录，服务器没有进行操作。",
  406: "请求的格式不可得。",
  410: "请求的资源被永久删除，且不会再得到的。",
  422: "当创建一个对象时，发生一个验证错误。",
  500: "服务器发生错误，请检查服务器。",
  502: "网关错误。",
  503: "服务不可用，服务器暂时过载或维护。",
  504: "网关超时。",
};

/**
 * 错误异常处理程序
 */
const errorHandler = async (error: { response?: Response; data?: any }) => {


  const { response, data } = error || {};
  if (!response) {
    message.error("您的网络发生异常，无法连接服务器");
    throw error;
  }

  const { status, statusText } = response;
  let backendMsg = data?.message || data?.msg;

  // 尝试从响应体中读取错误信息（可能已被 umi-request 解析过）
  if (!backendMsg && response?.clone) {
    try {
      const parsed = await response.clone().json();
      backendMsg = parsed?.message || parsed?.msg;
    } catch (e) {
      // ignore json parse error and fallback to默认文案
    }
  }

  const msg = backendMsg || codeMessage[status] || statusText || "请求错误";
  message.error(msg);

  if (status === 401) {
    // 与后端约定不带token访问时报错401，当报错401时重定向到登录页面
    sessionStorage.clear();
    localStorage.removeItem("token");
    history.push("/user/login");
  }

  throw error; // 保留 error 以便调用方能获取后端返回的错误信息
};
const filterParams = (params: any) => {
  if (typeof params !== "object" || params === null) {
    return params;
  }

  const newParams: any = {};

  // 遍历对象的所有键
  for (const key in params) {
    // 确保 key 是对象自身的属性，而不是继承来的
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];

      // 核心过滤逻辑：值既不是 null 也不是 undefined 时才保留
      if (value !== null && value !== undefined) {
        newParams[key] = value;
      }
    }
  }

  return newParams;
};

/*
 * 配置request请求时的默认参数
 */
const request = extend({
  errorHandler, // 默认错误处理
  timeout: 1000 * 60 * 5, // 全局请求超时时间 5 分钟
});

// request拦截器, 携带token.
request.interceptors.request.use((url: string, options: RequestOptionsInit) => {
  // 不携带token的请求数组
  let notCarryTokenArr: string[] = [];
  let prefix = process.env.API_HOST_URL;
  // let prefix = "/test";
  if (url !== "/paas-common-auth/code" && !url.startsWith("/system")) {
    prefix = process.env.API_HOST_URL + "/yzb-toubiao-ent";
  }

  if (notCarryTokenArr.includes(url)) {
    return {
      url,
      options,
    };
  }
  // 给每个请求带上token
  let headers = {
    Authorization: "Bearer " + getUserToken(),
    //apikey: "web-user",
    TraceId: new Date().getTime().toString(),
  };

  if (options.method === "get") {
    options.params = filterParams(options.params);
  }

  return {
    url: prefix + url,
    options: { ...options, interceptors: true, headers },
  };
});

// 业务错误统一提示（HTTP 200 但 code 非 200）
request.interceptors.response.use(async (response, options) => {
  if (!response?.ok) {
    return response;
  }

  const contentType = response.headers.get("content-type") || "";
  const isJSON = contentType.includes("application/json");
  const skipCheck =
    options?.getResponse ||
    options?.responseType === "blob" ||
    options?.responseType === "arrayBuffer" ||
    !isJSON;

  if (skipCheck) {
    return response;
  }

  try {
    const cloned = await response.clone().json();
    const code = Number(cloned?.code);
    if (!Number.isNaN(code) && code !== 200) {
      if (code === 401){
        sessionStorage.clear();
        localStorage.removeItem("token");
        history.push("/user/login");
        return response;
      }
      const msg =
        cloned?.message ||
        cloned?.msg ||
        codeMessage[code] ||
        "请求出错，请稍后重试";
      message.error(msg);
    }
  } catch (e) {
    // 忽略解析失败，交由调用方处理
  }

  return response;
});

const getUserToken = (): string => {
  let userToken: string = "";
  if (localStorage.getItem("token")) {
    userToken = localStorage.getItem("token") || "";
  } else {
    userToken = sessionStorage.getItem("token") || "";
  }
  return userToken;
};

/**
 * @url 请求的url
 * @parameter 上传的参数
 */

// 封装的get,post,put,delete请求  参数{params ：{}}
//当涉及下载附件时 参数中需要添加responseType:'blob',getResponse:true
export const get = async (url: string, parameter?: any): Promise<any> => {
  try {
    const res = await request(url, { method: "get", ...parameter });
    return res;
  } catch (error) {
    throw error;
  }
};

export const deletes = async (
  url: string,
  parameter?: Record<string, unknown>
): Promise<any> => {
  try {
    const res = await request(url, { method: "delete", ...parameter });
    return res;
  } catch (error) {
    throw error;
  }
};

/*post 与 put请求的参数格式：
{
  data:传往后端的参数，
  requestType: 'form' 控制 'Content-Type': 'application/x-www-form-urlencoded; 为这个
}
*/
export const post = async (
  url: string,
  args?: RequestOptionsInit
): Promise<any> => {
  try {
    const res = await request(url, {
      method: "post",
      ...args,
    });
    return res;
  } catch (error) {
    throw error;
  }
};

export const put = async (
  url: string,
  args: RequestOptionsInit
): Promise<any> => {
  try {
    const res = await request(url, { method: "put", ...args });
    return res;
  } catch (error) {
    throw error;
  }
};

export const patch = async (
  url: string,
  args: RequestOptionsInit
): Promise<any> => {
  try {
    const res = await request(url, { method: "patch", ...args });
    return res;
  } catch (error) {
    throw error;
  }
};

export default {
  get,
  post,
  put,
  deletes,
  patch,
};
